import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { createLobby, getCategories } from '../lib/firebase/collections';
import { useAuth } from '../contexts/AuthContext';
import { getStoredPlayerName, setStoredPlayerName } from '../utils/userUtils';
import type { CardCategory, GameSettings } from '../types/game';

export default function Home() {
  const navigate = useNavigate();
  const [joinCode, setJoinCode] = useState('');
  const { user, loading: authLoading } = useAuth();
  const [playerName, setPlayerName] = useState(() => getStoredPlayerName());
  const [isCreating, setIsCreating] = useState(false);
  const [categories, setCategories] = useState<CardCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    if (user && !playerName && user.displayName) {
      setPlayerName(user.displayName);
    }
    
    // Kategorien laden
    loadCategories();
  }, [user, playerName]);

  const loadCategories = async () => {
    try {
      const fetchedCategories = await getCategories();
      setCategories(fetchedCategories);
      
      // Setze die erste verfügbare Kategorie als Standard
      if (fetchedCategories.length > 0) {
        setSelectedCategoryId(fetchedCategories[0].id);
      } else {
        // Fallback auf die bekannte Kategorie-ID
        setSelectedCategoryId('ffZQ6P8VtO6epfqt9fQ6');
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien:', error);
      // Fallback auf die bekannte Kategorie-ID
      setSelectedCategoryId('ffZQ6P8VtO6epfqt9fQ6');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCreateLobby = async () => {
    if (!selectedCategoryId) return alert("Bitte wähle eine Kategorie.");
    if (!playerName.trim()) return alert("Bitte gib einen Spielernamen ein.");

    setStoredPlayerName(playerName.trim());
    setIsCreating(true);
    try {
      const defaultSettings: GameSettings = {
        timerEnabled: true,
        roundTimeSeconds: 90,
        winningScore: 10,
        maxRounds: undefined,
        allowLateJoin: true,
        showPlayerNames: false,
        autoStartNextRound: true,
        handSize: 7,
        allowSkipCards: true,
      };
      const lobbyId = await createLobby(
        playerName,
        user && user.uid ? user.uid : 'temp',
        selectedCategoryId,
        defaultSettings
      );
      navigate(`/lobby/${lobbyId}`);
    } catch (error) {
      console.error("Fehler beim Erstellen der Lobby:", error);
      alert("Lobby konnte nicht erstellt werden.");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 p-3 sm:p-4">
      <div className="w-full max-w-md rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 lg:p-10 bg-white/10 backdrop-blur-xl border border-white/20 flex flex-col gap-6 sm:gap-8" style={{ boxShadow: '0 8px 32px 0 rgba(16, 185, 129, 0.25)' }}>
        <div className="flex flex-col items-center gap-2">
          <span className="text-3xl sm:text-4xl">🃏</span>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white drop-shadow mb-2 text-center leading-tight break-words">
            Cards Against Humanity Online
          </h1>
        </div>
        
        <div className="flex flex-col gap-4">
          <div className="w-full">
            <label htmlFor="playerName" className="block text-base sm:text-lg font-semibold text-primary-300 mb-2">Dein Name</label>
            <input
              type="text"
              id="playerName"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="input text-center text-lg sm:text-xl"
              placeholder="Wie lautet dein Name?"
              required
            />
          </div>
          
          {/* Kategorie-Auswahl */}
          <div className="w-full">
            <label className="block text-base sm:text-lg font-semibold text-primary-300 mb-2">Wähle ein Kartenset</label>
            {loadingCategories ? (
              <p className="text-sm text-gray-400">Lade Kategorien...</p>
            ) : (
              <div className="relative">
                <select
                  value={selectedCategoryId}
                  onChange={e => setSelectedCategoryId(e.target.value)}
                  className="w-full appearance-none rounded-lg px-4 py-3 bg-white/20 text-primary-200 focus:outline-none focus:ring-2 focus:ring-primary-400 font-semibold text-sm sm:text-base lg:text-lg cursor-pointer pr-10"
                  style={{ minHeight: '3.25rem' }}
                  aria-label="Kategorie auswählen"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name} {cat.description ? `– ${cat.description}` : ''}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-primary-300 text-lg sm:text-xl">▼</span>
              </div>
            )}
          </div>
          
          <button
            className="w-full rounded-lg sm:rounded-xl py-3 sm:py-4 bg-gradient-to-r from-green-500 to-green-700 text-white font-bold text-base sm:text-lg shadow-green-500/30 shadow-lg hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-wait"
            onClick={() => handleCreateLobby()}
            disabled={isCreating || authLoading || !playerName.trim() || !selectedCategoryId}
          >
            {isCreating ? 'Lobby wird erstellt...' : 'Neues Spiel starten'}
          </button>
        </div>

        <div className="relative flex py-2 items-center">
            <div className="flex-grow border-t border-gray-600"></div>
            <span className="flex-shrink mx-4 text-gray-400 text-sm sm:text-base">Oder</span>
            <div className="flex-grow border-t border-gray-600"></div>
        </div>

        <div className="flex flex-col gap-2 sm:gap-3">
          <input
            type="text"
            placeholder="Beitrittscode eingeben"
            value={joinCode}
            onChange={e => setJoinCode(e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-gray-900/80 text-white border border-green-400 focus:outline-none focus:ring-2 focus:ring-green-500 placeholder-gray-400 text-sm sm:text-base"
          />
          <button
            className="w-full py-3 rounded-lg sm:rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold shadow-md transition-all text-sm sm:text-base"
            onClick={() => joinCode && navigate(`/lobby/${joinCode}`)}
            disabled={!joinCode.trim()}
          >
            Bestehendem Spiel beitreten
          </button>
        </div>
        
        {categories.length === 0 && (
          <div className="text-center text-yellow-300 text-xs sm:text-sm">
            Keine Kategorien verfügbar. Besuche das <a href="/admin" className="underline">Admin Panel</a> um Beispielkarten zu importieren.
          </div>
        )}
      </div>
    </div>
  );
} 